/**
 * 接口请求封装
 * */
import axios from 'axios'
import { reportSGM } from '@jdt-jinke/jdpay_utils'
import type { GatewayOptions, RequestOptions, FetchOptions } from './types'
import {
  defaultRequestOptions,
  defaultGatewayOptions,
  defaultFetchOptions
} from './constant'

const API_HOST = import.meta.env.VITE_API_HOST
const SET_ID = import.meta.env.VITE_MOCK_SETID
const JD_LOGIN_URL = import.meta.env.VITE_LOGIN_URL || ''

/**
 * 发起请求通用方法
 * @param {string} baseUrl
 * @param {number} timeout
 */
export default class Request {
  /** 实例方法入参 */
  config: RequestOptions = defaultRequestOptions

  /** 网关配置 */
  gw: GatewayOptions = defaultGatewayOptions

  /** 是否需要loading */
  loading: boolean = false

  /** Header 头信息 */
  private headers: Record<string, string> = {
    'Content-Type': 'application/json;charset=UTF-8'
  }

  /** 实例化接口请求 */
  constructor(options: RequestOptions) {
    console.log('--------实例化接口请求：', options)
    // 1.合并配置
    this.config = { ...this.config, ...options }

    // 2.设置默认 BaseURL
    if (!options.baseURL) this.setDefaultBaseURL()

    console.log('查看接口配置信息----', this.config)

    // 3.设置默认公钥请求地址
    this.setDefaultPubkeyURL()

    // 4.添加 MOCK 数据的 header 头
    SET_ID && this.addHeader({ setId: SET_ID })
  }

  /** 获取科技网关默认地址 */
  setDefaultBaseURL() {
    this.config.baseURL = SET_ID ? API_HOST || '' : this.getJddBaseURL()
  }

  /** 设置config */
  setConfig(config: RequestOptions = {}) {
    // 1.合并配置
    this.config = { ...this.config, ...config }
    !this.config.baseURL && this.setDefaultBaseURL()
  }

  /** 获取科技网关地址 */
  getJddBaseURL() {
    const { biz, env } = this.config
    const name = {
      test: 'test',
      pre: 'inner',
      pro: ''
    }
    return `//ms${name[env || 'pro'] || ''}.jr.jd.com/gw2/generic/${biz}/newh5/m/`
  }

  /** 获取科技网关地址 */
  setDefaultPubkeyURL() {
    const { env, pubKeyUrl } = this.config
    if (pubKeyUrl) return
    this.config.pubKeyUrl = `//ms${env == 'test' ? 'test' : ''}.jr.jd.com/gw/generic/getRSAPublicKey`
  }

  /** 添加 header 头 */
  addHeader(header: Record<string, string>): void {
    if (header && typeof header === 'object' && !Array.isArray(header)) {
      this.headers = { ...this.headers, ...header }
      console.log('-------设置header头--------', this.headers)
    } else {
      console.error('addHeader 方法的参数必须是一个对象')
    }
  }

  /** 获取 sgmcode */
  getSGMCode = (str: string = ''): string =>
    `${this.config?.biz || 'biz'}_${str || ''}`

  /** 获取公钥 */
  async initPubKey() {
    if (!window?.cryptico) return
    try {
      // 调用接口获取公钥
      const res: any = await this.exec(
        { method: 'get', url: '', dataType: 'json' },
        true
      )

      // 如果采用非国密算法，需要初始化公钥配置
      if (res && window.cryptico) {
        // @ts-ignore 把公钥放在全局变量里
        window.PUBKEY = res
        const publicKeyStr = typeof res === 'string' ? res : res.publicKey
        if (publicKeyStr) {
          window.cryptico.setPublicKeyString(publicKeyStr)
        }
      }
    } catch (err) {
      reportSGM({ code: 'initPubKey_abnormal', type: 2, err })
    }
  }

  /** 创建请求实例 */
  create(header = {}, getPubkey: boolean) {
    const headers = Object.assign(this.headers, header)
    const { timeout, withCredentials, pubKeyUrl, baseURL } = this.config

    console.log('create 接口请求：', this.config)

    return axios.create({
      headers: getPubkey ? header : headers,
      baseURL: getPubkey ? pubKeyUrl : baseURL,
      timeout,
      withCredentials
    })
  }
  /** 国密加密 */
  summberEncypt(data: string) {
    try {
      // @ts-ignore 使用 as 关键字将 window 断言为 CustomWindow 类型
      const customWindow = window as CustomWindow
      console.log(
        '----------------summberEncypt start ------------',
        data,
        customWindow.PUBKEY
      )
      // 加密依赖项校验
      const { publicKey } = customWindow.PUBKEY || {}
      if (publicKey && customWindow?.SummerCryptico?.encryptData) {
        data =
          publicKey && customWindow.SummerCryptico?.encryptData(publicKey, data)
      }
      console.log('----------------summberEncypt end ------------', data)
    } catch (error) {
      console.log('----------------summberEncypt error ------------', error)
    }

    // 返回加密数据
    return {
      encFlag: !!data,
      data: data
    }
  }

  /** 国密解密 */
  summerDecypt(data: string, { flagKey = 'encflag', dataKey = 'data' } = {}) {
    // @ts-ignore 使用 as 关键字将 window 断言为 CustomWindow 类型
    const customWindow = window as CustomWindow
    // 执行加密
    const res =
      customWindow?.SummerCryptico?.decryptData &&
      customWindow.SummerCryptico.decryptData(data)
    // 加密成功赋值
    const encyptSucc = !!res
    // 打印加密结果
    console.info(
      '%c summerDecypt 解密结果 ',
      'color:#fff;font-size:14px;background:linear-gradient(to right, green, gold);',
      encyptSucc,
      res
    )
    // 返回加密信息
    return {
      [flagKey]: encyptSucc ? 1 : 0,
      [dataKey]: res || data
    }
  }

  /** 普通加密 */
  encrypt(data: string, { flagKey = 'encflag', dataKey = 'data' } = {}) {
    // @ts-ignore 使用 as 关键字将 window 断言为 CustomWindow 类型
    const customWindow = window as CustomWindow

    // 执行加密方法
    const res =
      customWindow?.cryptico?.encryptData &&
      customWindow.cryptico.encryptData(data)
    // 加密成功赋值
    const encyptSucc = !!res?.status && !!res?.cipher

    console.info(
      '%c 普通加密结果 ',
      'color:#fff;font-size:14px;background:linear-gradient(to right, green, gold);',
      encyptSucc,
      res
    )

    if (!encyptSucc) {
      reportSGM({
        code: `encryptDataError_encryptFail`,
        desc: 'encyptSucc is false, encrypt failed'
      })
    }

    // 返回加密后的数据
    return {
      encyptSucc,
      data: {
        [flagKey]: encyptSucc ? 1 : 0,
        [dataKey]: encyptSucc ? res.cipher : data
      }
    }
  }

  /** 普通解密 */
  decrypt(data: string) {
    // 数据格式校验
    if (typeof data !== 'string') return data
    // 解密依赖校验
    if (!window.cryptico || !window.cryptico.decryptData) return data
    // 执行解密
    const res = window.cryptico.decryptData(data)
    console.info(
      '%c 普通解密结果 ',
      'color:#fff;font-size:14px;background:linear-gradient(to right, green, gold);',
      res
    )
    // 返回解密后的数据
    return res?.plaintext || data || '{}'
  }

  /** 加密数据 */
  encyptData(data: any) {
    // 获取加密的网关配置信息
    const { encypt = this.encrypt, encyptConfig } = this.gw
    // 如果没有数据或没有加密方法，直接返回
    const { flagKey = '', dataKey = '' } = encyptConfig || {}
    // 如果没有加密标识，直接返回，不加密数据
    if (!flagKey || !dataKey) return { encyptSucc: false, data }

    // 将业务数据转化成 string
    if (typeof data === 'object') data = JSON.stringify(data)
    // 根据配置设置加密返回默认值
    const enData = {
      [flagKey]: 0,
      [dataKey]: data
    }

    // 加密依赖校验
    if (!data || !encypt) return { encyptSucc: false, data: enData }

    // 进行加密，并赋值
    // console.log('--------加密方法----------', this.gw, data, enData)

    return encypt(data, encyptConfig)
  }

  /** 解密数据 */
  decyptData(data: any = {}, options?: FetchOptions) {
    // 不需要解密处理
    const { decypt = this.decrypt } = this.gw
    // console.info(
    //   '%c 解密数据 ',
    //   'color:#fff;font-size:14px;background:linear-gradient(to right, green, gold);',
    //   data,
    //   decypt
    // )
    if (typeof data.data !== 'string') {
      console.info(
        '%c 解密失败 data is not string ',
        'font-size:14px;color:#fff;background:linear-gradient(to right, red, orange );',
        data,
        decypt
      )
      reportSGM({
        code: `decryptDataError_dataNotString`,
        res: { data, options },
        type: 2,
        desc: 'data.data in not string, cannot decrypt'
      })
      return data
    }
    if (typeof decypt !== 'function') {
      console.info(
        '%c 解密失败 decrypt is not function ',
        'font-size:14px;color:#fff;background:linear-gradient(to right, red, orange );',
        data,
        decypt
      )
      reportSGM({
        code: `decryptDataError_decyptNoFunction`,
        res: { decypt: decypt },
        desc: 'decypt in not a function'
      })
      return data
    }
    try {
      return {
        ...data,
        data: decypt(data.data)
      }
    } catch (err) {
      console.info(
        '%c 解密失败 decrypt fail ',
        'font-size:14px;color:#fff;background:linear-gradient(to right, red, orange );',
        err,
        data,
        decypt
      )
      reportSGM({
        code: `decryptDataError_decyptFail`,
        err,
        desc: 'decrypt run failed'
      })

      return data
    }
  }

  /** 设置网关配置
   * @param {string} successCode
   * @param {string} notLoginCode
   * @param {string} dataType
   */
  setGW(config = {}) {
    this.gw = Object.assign(this.gw, config)
  }

  /** 跳转登录页 */
  jumpLogin() {
    const loginUrl = `${JD_LOGIN_URL || '//plogin.m.jd.com/user/login.action'}?appid=300&returnurl=${encodeURIComponent(window.location.href)}`
    console.log('jumpLogin:', JD_LOGIN_URL, loginUrl)
    window.location.href = loginUrl
  }

  /** 执行请求的方法
   * @param {*} url 请求地址
   */
  async exec(options: FetchOptions, getPubkey: boolean = false) {
    /** 是否加密成功 */
    let encryptSucc = false

    /** 聚合options */
    options = { ...defaultFetchOptions, ...options }

    // console.log('接口请求 exec: ', options)

    const { url, params, method = '', headers, encrypt, loading } = options

    /** 缓存是否需要处理 loading的状态 */
    this.loading = loading || false

    /** 组装请求参数 */
    const reqInfo = { url, data: params, method: method.toLowerCase() }

    /** 业务入参加密 */
    if (encrypt) {
      // console.info(
      //   '%c 加密数据 ',
      //   'color:#fff;font-size:14px;background:linear-gradient(to right, red, gold);',
      //   reqInfo,
      //   params
      // )
      const encryptedData = this.encyptData(params)
      encryptSucc = !!encryptedData?.encyptSucc
      reqInfo.data = encryptedData?.data

      if (!encryptSucc) {
        console.info(
          '%c 加密失败',
          'font-size:14px;color:#fff;background:linear-gradient(to right, red, orange );',
          encryptedData?.data
        )
      }
    }

    /** 入参转化 */
    const { transformRequest } = this.gw
    if (typeof transformRequest === 'function')
      reqInfo.data = await transformRequest(reqInfo.data)

    /** 创建请求实例 */
    const instance = this.create(headers, getPubkey)

    /** 打印参数 */
    console.info(
      '%c 请求信息' +
        `${encrypt ? '(加密) ' : ' '}` +
        `${getPubkey ? '：获取公钥' : url}`,
      'color:#fff;font-size:14px;background:linear-gradient(to right, #4776e6, #8e54e9);',
      params,
      options,
      reqInfo
    )

    /** 返回处理后的接口请求 */
    return new Promise((resolve, reject) => {
      instance(reqInfo)
        .then(({ data }) => {
          try {
            // 响应转化
            const { transformResponse } = this.gw
            if (typeof transformResponse === 'function')
              data = transformResponse(data, options)

            // 解密数据
            encryptSucc && (data = this.decyptData(data, options))

            // 未登录 code 码处理
            if (data.code === this.gw.notLoginCode) return this.jumpLogin()

            // 异常处理, 强依赖接口才展示错误页 TODO:
            if (data.code !== this.gw.successCode || !data.data) {
              reject({
                type: 'fail',
                options,
                ...data
              })
              reportSGM({
                code: 'networkGatewayError',
                req: options,
                res: data.data
              })
              return
            }

            // 解析业务数据并重新赋值
            if (encrypt && typeof data?.data === 'string') {
              data.data = JSON.parse(data.data)
            }

            reportSGM({
              type: 3,
              code: this.getSGMCode('apiCallInfo'),
              req: {
                params: options.params,
                url: options.url
              },
              res: data.data
            })

            console.info(
              '%c ' + url + ' 业务层返回结果===>',
              'color:#fff;font-size:14px;background:linear-gradient(to right, #CC8C4E, #c37223);',
              data
            )

            resolve(data.data)
          } catch (error) {
            // 异常处理, 强依赖接口才展示错误页 TODO:
            this.fail(error, options, reject, data) // 失败的处理
          }
        })
        .catch((err) => {
          // 异常处理, 强依赖接口才展示错误页 TODO:
          this.fail(err, options, reject)
        })
    })
  }

  /** 响应失败的处理 */
  fail(
    err: any,
    options: FetchOptions,
    reject: (reason?: any) => void,
    data?: object
  ): void {
    console.info(
      '%c ' + (options?.url || '') + ' 接口请求异常 ',
      'font-size:14px;color:#fff;background:linear-gradient(to right, red, orange );',
      err,
      data
    )

    // 请求失败时,根据业务判断状态
    const code = data ? 'abnormal' : 'network'

    // 返回失败
    reject({
      type: 'fail',
      code,
      err,
      options,
      referUrl: document.referrer,
      message: data ? '业务处理异常，请退出重试' : '网络繁忙，请稍后重试'
    })

    // 埋点上报
    try {
      reportSGM({
        code: `Request_${code}CatchError`,
        type: 2,
        err,
        req: options,
        res: { ...(data || {}), code }
      })
    } catch (error) {
      console.log(error)
    }
  }
}
